import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ColumnDef } from '@tanstack/react-table';
import { useIntl } from 'react-intl';

import { eventTypeBadgeOptions } from 'src/features/eventLog/utils/constants';
import { EVENT_TYPE } from 'src/features/eventLog/types';
import { StatisticEvent } from 'src/features/home/types';
import { homeMessages } from 'src/features/home/utils/messages';
import { GeneralBlock } from 'src/registry/components/blocks/GeneralBlock';
import StatisticTable from 'src/registry/components/statistic/StatisticTable';
import { Badge } from 'src/registry/ui/Badge';
import { ICON_MAP } from 'src/utils/constants';
import { cn } from 'src/utils/shadcn';
import { useSidePanel } from 'src/registry/providers/SidePanelProvider';
import GeneralMessageBlock from 'src/registry/components/blocks/GeneralMessageBlock';

type Props = {
    data?: StatisticEvent[];
    totalEvents?: number;
    isError?: boolean;
};
export default function WidgetEvents({ data, totalEvents, isError }: Props) {
    const { formatMessage, locale } = useIntl();

    const { sidePanelState } = useSidePanel();

    const hasOpenSidePanel = Object.values(sidePanelState).includes(true);

    const columns: ColumnDef<StatisticEvent>[] = [
        {
            accessorKey: 'timestamp',
            header: formatMessage(homeMessages.time),
            cell: (info) =>
                new Date(info.getValue() as string).toLocaleTimeString(locale, {
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                    hour12: false,
                }),
            maxSize: 60,
        },
        {
            accessorKey: 'event_type',
            header: formatMessage(homeMessages.type),
            cell: (info) => {
                const option = eventTypeBadgeOptions[info.getValue() as EVENT_TYPE];
                const { iconColor } = option?.color ?? { iconColor: undefined };

                return (
                    <Badge
                        theme={option?.theme || 'gray'}
                        className={cn(
                            'flex min-w-0 items-center gap-1 sm:max-w-72 md:max-w-44 2xl:max-w-72',
                            hasOpenSidePanel && 'md:max-w-24',
                        )}
                    >
                        {option?.icon && (
                            <FontAwesomeIcon
                                icon={option.icon}
                                className={cn('z-0', !iconColor && !option.theme && 'opacity-50')}
                                style={{ color: iconColor }}
                                size={option.iconSize}
                            />
                        )}
                        <span className="min-w-0 truncate">{formatMessage(option.label)}</span>
                    </Badge>
                );
            },
        },
        {
            accessorKey: 'event_data',
            header: formatMessage(homeMessages.identifier),
            cell: (info) => {
                const eventData = info.getValue() as StatisticEvent['event_data'];

                if (!eventData?.identifiers) return '-';

                return eventData.identifiers[0]?.value;
            },
        },
    ];

    return (
        <GeneralBlock className="flex-1 gap-6">
            <div className="flex gap-6">
                <div className="flex size-14 items-center justify-center rounded-full border-8 border-green-50 bg-green-100 p-1 text-2xl text-green-700">
                    <FontAwesomeIcon icon={ICON_MAP.TREND_UP} />
                </div>
                <div className="flex flex-col">
                    <span className="text-sm font-semibold text-gray-600">
                        {formatMessage(homeMessages.totalEventsToday)}
                    </span>
                    <span className="text-lg font-bold">{isError ? '-' : totalEvents}</span>
                </div>
            </div>
            {isError ? (
                <GeneralMessageBlock variant="error" />
            ) : (
                <StatisticTable
                    title={formatMessage(homeMessages.lastTenEvents)}
                    data={data}
                    columns={columns as any}
                />
            )}
        </GeneralBlock>
    );
}
